import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { catchError, Subject, tap } from 'rxjs';
import { ConfigService } from '../../core/config.service';
import { DisplayOptions, Menu } from '../../core/display-options';
import { DisplayOptionsService } from '../../core/display-options.service';
import { SilentError } from '../../core/error';
import { SessionData } from '../../core/session-data';
import { SessionService } from '../../core/session.service';
import { MessageBoxStyle, MessageBoxContentTypes } from '../../shared/message-box/message-box-content';
import { MessageBoxService } from '../../shared/message-box/message-box.service';
import { AlternativeCurrenciesService } from '../shared/alternative-currencies.service';
import { AlternativeCurrenciesTypes } from '../shared/models/alternative-currency/alternative-currencies-types.enum';

@Component({
  standalone: false,
  selector: 'car-header',
  templateUrl: './header.component.html',
})

export class HeaderComponent implements OnInit {
  private showDebugConsole: boolean;
  private ngUnsubscribe: Subject<void> = new Subject<void>();

  showHeader: boolean;
  showDefaultHeader: boolean;
  showDriHeader: boolean;
  showSorHeader: boolean;
  showSorMobileNavBar: boolean;
  isMenuCollapsed = true;
  pointsAlternativeCurrency: any;
  pointsAlternativeCurrencyArray = [];
  dbcAlternativeCurrency: any;
  dbcAlternativeCurrencyArray = [];
  loyaltyAlternativeCurrency: any;
  loyaltyAlternativeCurrencyArray = [];
  isCultureDialogOpen: boolean;
  isNavbarExpanded: boolean;
  wrapNavMenu: boolean;
  mainLogo: string;
  programPhoneNumber: string;
  isHamburguerMenu: boolean = false;
  isPhoneEndMenu: boolean = false;
  isAccountSettingsLabelEndMenu: boolean = false;
  logoTargetUrl: string;
  loyaltyLabel: string;

  private marginCurrencyLastValue: number;
  private pointsLastValue: any;
  private loyaltyLastValue: any;

  private loadLoyaltyError: any;

  sessionData: SessionData;
  displayOptions: DisplayOptions;
  constructor(
    private displayOptionsService: DisplayOptionsService,
    private sessionService: SessionService,
    private acService: AlternativeCurrenciesService,
    private changeDetectionRef: ChangeDetectorRef,
    private configService: ConfigService,
    private messageBoxService: MessageBoxService,
  ) {
    this.displayOptions = this.displayOptionsService.getOptions();
    this.sessionData = this.sessionService.getSessionData();
    this.showDebugConsole = configService.getConfigData().tealium.environment != "prod";
  }

  ngOnInit() {

    if (this.showDebugConsole) {
      console.log("API Url", this.sessionData.apiUrl);
      console.log("Display Options", this.displayOptions);
      console.log("Session", this.sessionData);
    }

    this.showHeader = this.displayOptions && Object.keys(this.displayOptions).length > 0;

    if (!this.showHeader) {
      return;
    }

    this.loyaltyLabel = this.displayOptions && this.displayOptions.labels.reward_credit_points;


    if (this.isPointsSpendAllowed) {
      this.acService.getBalance(AlternativeCurrenciesTypes.Points)
        .pipe(catchError(error => { throw new SilentError(error.message); }))
        .subscribe(() => this.changeDetectionRef.detectChanges());
    }

    if (this.isMarginCurrencyAllowed(AlternativeCurrenciesTypes.DBC)) {
      this.acService.getBalance(AlternativeCurrenciesTypes.DBC)
        .pipe(catchError(error => { throw new SilentError(error.message); }))
        .subscribe(() => this.changeDetectionRef.detectChanges());
    }

    if (this.isLoyaltyCurrencyAllowed) {
      this.acService.getBalance(AlternativeCurrenciesTypes.Loyalty)
        .pipe(
          tap(_ => { this.loadLoyaltyError = false; }),
          catchError(error => { this.loadLoyaltyError = true; throw new SilentError(error.message); }))
        .subscribe(() => this.changeDetectionRef.detectChanges());
    }

    this.pointsAlternativeCurrency = this.sessionData.memberInfo.getAlternativeCurrency(AlternativeCurrenciesTypes.Points);

    this.loyaltyAlternativeCurrency = this.sessionData.memberInfo.getAlternativeCurrency(AlternativeCurrenciesTypes.Loyalty);

    // #TODO: refactor this into session-data.ts as a public function
    this.pointsAlternativeCurrencyArray = this.sessionData.memberInfo.alternative_hard_currencies.filter(c =>
      c.type === AlternativeCurrenciesTypes.Points && (c.redeem_for_car === true || c.redeem_for_car_online === true));

    this.loyaltyAlternativeCurrencyArray = this.sessionData.memberInfo.alternative_hard_currencies.filter(c =>
      c.type === AlternativeCurrenciesTypes.Loyalty && (c.redeem_for_car === true || c.redeem_for_car_online === true));

    this.dbcAlternativeCurrency = this.sessionData.memberInfo.getAlternativeMarginCurrency(AlternativeCurrenciesTypes.DBC);

    this.dbcAlternativeCurrencyArray = this.sessionData.memberInfo.alternative_margin_currencies.filter(c =>
      c.type === AlternativeCurrenciesTypes.DBC && (c.redeem_for_car === true));

    this.programPhoneNumber = this.displayOptions.program_phone_number;
    this.logoTargetUrl = "/";

    this.setCustomsByClubName(this.displayOptions.program_short_name);

    this.showDriHeader = this.displayOptions && this.displayOptions.options && (this.displayOptions.options.is_dri === "true");
    this.showSorHeader = this.displayOptions && this.displayOptions.optionsId && (this.displayOptions.optionsId.startsWith("c1:e3"));
    if (!this.showDriHeader && !this.showSorHeader) {
      this.showDefaultHeader = true;
    }

    if (this.showSorHeader) {
      this.showSorMobileNavBar = true;
      if (this.displayOptions.logos) {
        this.displayOptions.logos.forEach(object => {
          if (object.logo_number === 1) {
            this.mainLogo = object.logo_image_url;
          }
        });
      }

      if (this.displayOptions.menus) {
        this.displayOptions.menus.forEach(menu => {
          if (menu.sub_menus) {
            menu.collapsed = true;
          }
        });
      }
    }

    this.wrapNavMenu = this.displayOptions.menus.length > 3;
  }

  public get showPointsInHeader() {
    if (this.skipBalanceCheck) return false;
    return this.points > 0;
  }

  public get showDBCInHeader() {
    return this.isMarginCurrencyAllowed(AlternativeCurrenciesTypes.DBC) && this.dbc > 0;
  }

  public get points(): number {

    var pointsType: AlternativeCurrenciesTypes = null;

    if (this.isPointsSpendAllowed) {
      pointsType = AlternativeCurrenciesTypes.Points;
    }

    if (pointsType == null) {
      return undefined;
    }

    const acBalance =
      this.acService.getBalanceCached(pointsType);

    var result = acBalance ? (acBalance.balance || 0) : 0;

    if (this.showDebugConsole && result != this.pointsLastValue && acBalance !== undefined) {
      console.log("Points", acBalance);
    }

    this.pointsLastValue = result;

    return result;
  }

  public get loyalty(): string {

    if (this.loadLoyaltyError) return "Error";

    return this.loadLoyalty();
  }

  public loadLoyalty() {

    var loyaltyType: AlternativeCurrenciesTypes = null;

    if (this.isLoyaltyCurrencyAllowed) {
      loyaltyType = AlternativeCurrenciesTypes.Loyalty;
    }

    if (loyaltyType == null) {
      return undefined;
    }

    if (this.acService.getIsLoyaltyLoading()) {
      return "...";
    }

    const acBalance =
      this.acService.getBalanceCached(loyaltyType);

    if (!acBalance) return "Error";

    var result = (acBalance.balance || 0);

    if (this.showDebugConsole && result != this.loyaltyLastValue && acBalance !== undefined) {
      console.log("Loyalty = ", acBalance);
    }

    this.loyaltyLastValue = result;

    return result.toString();
  }

  public get dbc(): number {
    if (!this.isMarginCurrencyAllowed(AlternativeCurrenciesTypes.DBC)) {
      return undefined;
    }

    const acBalance = this.acService.getBalanceCached(AlternativeCurrenciesTypes.DBC);

    var result = acBalance ? (acBalance.balance || 0) : 0;

    if (this.showDebugConsole && result != this.marginCurrencyLastValue && acBalance !== undefined) {
      console.log(AlternativeCurrenciesTypes.DBC, acBalance);
    }

    this.marginCurrencyLastValue = result;

    return result;
  }

  public canShowPointsForDri(pointSubType: string): boolean {

    if (this.skipBalanceCheck) return false;

    const applypointsbalance = this.acService.getBalancesCached(AlternativeCurrenciesTypes.Points);
    if (applypointsbalance) {
      this.pointsAlternativeCurrencyArray.filter(points => {
        applypointsbalance.filter(balance => {
          if (points != null && points.sub_type != null
            && balance != null && balance.subtype != null
            && points.sub_type.toLowerCase() === balance.subtype.toLowerCase()) { points.balance = balance.balance }
        });
      });
    }
    var itemPoint = this.pointsAlternativeCurrencyArray.find(x => x.sub_type.toLowerCase() === pointSubType.toLowerCase());

    // is Agent View
    if (this.sessionData.isAgent) {
      if (itemPoint && itemPoint.redeem_for_car) {
        return itemPoint.redeem_for_car;
      }
    }

    else {
      if (itemPoint && itemPoint.redeem_for_car_online)
        return itemPoint.redeem_for_car_online;
    }

    return false;

  }

  public get skipBalanceCheck(): boolean {

    for (var point of this.pointsAlternativeCurrencyArray) {
      if (point.carRedemptionOptions && point.carRedemptionOptions.skip_points_verify)
        return true;
    }

    for (var loyalty of this.loyaltyAlternativeCurrencyArray) {
      if (loyalty.carRedemptionOptions && loyalty.carRedemptionOptions.skip_points_verify)
        return true;
    }

    return false;

  }

  private get isPointsSpendAllowed(): boolean | string {
    return this.sessionData.memberInfo
      && this.sessionData.memberInfo.pointsCurrency
      && this.sessionData.memberInfo.pointsCurrency.isPointsSpendAllowed(this.sessionData.isAgent);
  }

  private isMarginCurrencyAllowed(type: AlternativeCurrenciesTypes): boolean {
    return this.sessionData.memberInfo &&
      this.sessionData.memberInfo.marginCurrency &&
      this.sessionData.memberInfo
        .marginCurrency.isMarginCurrencyAllowed(type, this.sessionData.isAgent);
  }

  private get isLoyaltyCurrencyAllowed(): boolean | string {
    return this.sessionData.memberInfo
      && this.sessionData.memberInfo.loyaltyCurrency
      && this.sessionData.memberInfo.loyaltyCurrency.isLoyaltyAllowed(this.sessionData.isAgent);
  }

  private toggleMenuOpen() {
    this.isNavbarExpanded = !this.isNavbarExpanded;
    this.showSorMobileNavBar = !this.isNavbarExpanded;
  }

  private collapseExpandNavItems(navItem: Menu) {
    this.displayOptions.menus.forEach(menu => {
      if (menu.sub_menus && menu.text_link !== navItem.text_link) {
        menu.collapsed = true;
      }
    });
    navItem.collapsed = !navItem.collapsed;
  }

  private applyCultureChanges() {
    this.isCultureDialogOpen = false;
  }

  private findBrandMenus(menu: Menu[]): Menu[] {
    if (menu)
      return menu.filter(p => p.link_type == null);

    return [];
  }

  private findHomeNavMenus(menu: Menu[]): Menu[] {
    if (menu)
      return menu.filter(p => p.link_type != null);

    return [];
  }

  private findMobileBrandMenus(menu: Menu[]): Menu[] {
    if (menu)
      return menu.filter(p => p.link_type == null && p.link_href.length !== 0);

    return [];
  }

  private toggleCultureDialog() {
    //SOR only
    if (this.showSorHeader) {
      this.notSupportedlangMsg();
    } else {
      this.isCultureDialogOpen = !this.isCultureDialogOpen;
    }
  }

  private notSupportedlangMsg() {
    this.messageBoxService
      .show({
        title: 'car.common.languageCurrencyNotSupported.title',
        style: MessageBoxStyle.Normal,
        text: 'car.common.languageCurrencyNotSupported.text',
        yesButton: 'common.ok',
        type: MessageBoxContentTypes.Error
      });
  }

  private enableHamburguerMenu() {
    this.isHamburguerMenu = true;
  }

  private enablePhoneEndMenu() {
    this.isPhoneEndMenu = true;
  }

  private enableAccountSettingsMenu() {
    this.isAccountSettingsLabelEndMenu = true;
  }

  private setCustomsByClubName(clubName: string) {
    let clubNameLowerCase = clubName.toLowerCase();
    if (clubNameLowerCase.startsWith("away") || clubNameLowerCase.startsWith("avid")) {
      this.enableHamburguerMenu();
      this.enablePhoneEndMenu();
      this.addCallTextToPhoneNumber();
      this.setLogoUrlByFirstMenuItemHref();
      this.enableAccountSettingsMenu();
    }
  }

  private addCallTextToPhoneNumber() {
    this.programPhoneNumber = "CALL " + this.programPhoneNumber;
  }

  private setLogoUrlByFirstMenuItemHref() {
    if (this.displayOptions.menus && this.displayOptions.menus.length > 0) {
      this.logoTargetUrl = this.displayOptions.menus[0].link_href;
    }
  }
}
